import { injectable } from 'inversify';

import { IFileUploaded } from '@modulate/shared/src/models/file';
import { usePatientId } from '@src/hooks/get-id';
import api from '../axios-config';

export const FILES_SERVICE = Symbol.for('FILES_SERVICE');

export interface IFilesService {
  uploadFile(file: File, patientId: string): Promise<IFileUploaded>;
  readFileAsArrayBuffer(file: File): Promise<ArrayBuffer>;
}

@injectable()
export class FilesService implements IFilesService {
  uploadFile = async (file: File, patientId: string): Promise<IFileUploaded> => {
    try {
      const fileContent = await this.readFileAsArrayBuffer(file);
      const fileType = handleMissingFileType(file);
      const batchKey = `uploadBatch:${patientId}`;
      let braceBatchId = sessionStorage.getItem(batchKey);
      if (!braceBatchId) {
        braceBatchId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        sessionStorage.setItem(batchKey, braceBatchId);
      }

      const response = await api.post<{ fileUrl: string }>('/api/files/upload', fileContent, {
        headers: {
          'Content-Type': 'application/octet-stream',
          clientId: '1', // get from state when implemented
          patientId: patientId,
          braceId: braceBatchId,
          fileName: file.name,
          fileType: fileType,
        },
      });
      const fileUploaded: IFileUploaded = {
        url: response.data.fileUrl,
        name: file.name,
        type: file.type,
      };
      return fileUploaded;
    } catch (error) {
      console.error('File upload failed', error);
      throw error; // Ensure the promise is rejected
    }
  };

  readFileAsArrayBuffer = (file: File): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };
}

function handleMissingFileType(file: File): string {
  let fileType = file.type;
  if (!fileType) {
    if (file.name.endsWith('.stl')) {
      fileType = 'application/vnd.ms-pkistl'; // or 'application/sla', another common MIME type for STL files
    } else {
      fileType = 'application/octet-stream'; // Fallback for unknown types
    }
  }
  return fileType;
}
